/**
 * ClientDetailSheet — Drawer lateral per mostrar detalls d'un client.
 *
 * Consumeix ClientProfileContent (component autònom amb Tabs).
 * Usat des de ClientsPage quan es clica "Veure" a una fila.
 */

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ExternalLink, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ClientProfileContent } from './ClientProfileContent';
import type { Client } from '@/services/clients';

export interface ClientDetailSheetProps {
  /** Client to display */
  client: Client | null;
  /** Whether the sheet is open */
  open: boolean;
  /** Callback when the sheet should close */
  onClose: () => void;
  /** Default tab to show */
  defaultTab?: 'detalls' | 'missatges';
}

export function ClientDetailSheet({
  client,
  open,
  onClose,
  defaultTab = 'detalls',
}: ClientDetailSheetProps) {
  const navigate = useNavigate();

  if (!client) return null;

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent hideClose className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="text-left pb-2">
          <div className="flex items-center justify-between">
            <SheetTitle className="sr-only">Detalls del client</SheetTitle>
            <Button
              variant="ghost"
              size="sm"
              className="gap-2 text-xs"
              onClick={() => {
                onClose();
                navigate(`/clients/${client.id}`);
              }}
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Obrir pàgina completa
            </Button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-muted transition-colors"
              aria-label="Tancar"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
        </SheetHeader>

        <ClientProfileContent client={client} defaultTab={defaultTab} />
      </SheetContent>
    </Sheet>
  );
}
